import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import { createCaseVersionFromSource } from '../../lib/createCaseVersion';

const NewConceptionVersionModal = ({
  isOpen,
  onClose,
  caseData,
  ownerProfileId,
  hasFollowUpLotInProgress = false,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmAbandonLot, setConfirmAbandonLot] = useState(false);

  const currentVersion = caseData?.versionNumber ?? 1;
  const nextVersion = currentVersion + 1;

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setConfirmAbandonLot(false);
    }
  }, [isOpen, caseData?.id]);

  if (!isOpen) return null;

  const needsLotConfirm = hasFollowUpLotInProgress;
  const canSubmit = !needsLotConfirm || confirmAbandonLot;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!caseData?.id || !ownerProfileId) {
      setError(t('caseVersion.createErrorMissingContext'));
      return;
    }
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await createCaseVersionFromSource({
        sourceCaseId: caseData.id,
        ownerProfileId,
      });

      onClose();
      if (typeof onSuccess === 'function') {
        onSuccess(result);
      } else {
        window.location.href = `/case-detail/${result.newCaseId}?tab=phases`;
      }
    } catch (err) {
      console.error('[NewConceptionVersionModal]', err);
      setError(err.message || t('caseVersion.createErrorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-border">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t('caseVersion.modalTitle', { version: nextVersion })}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {caseData?.patientName} · {caseData?.caseNumber}
              {currentVersion > 1 ? ` · v${currentVersion}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label={t('caseVersion.cancel')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error ? (
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 flex items-start gap-3"
            >
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-destructive">{t('caseVersion.errorTitle')}</p>
                <p className="text-sm text-destructive/90 mt-1">{error}</p>
              </div>
            </div>
          ) : null}

          <p className="text-sm text-muted-foreground">{t('caseVersion.modalIntro')}</p>

          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('caseVersion.keptLabel')}
            </p>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                {t('caseVersion.keptScansPhotos')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                {t('caseVersion.keptPrescription')}
              </li>
            </ul>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">
              {t('caseVersion.restartedLabel')}
            </p>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-primary shrink-0" />
                {t('caseVersion.restartedDesign')}
              </li>
            </ul>
          </div>

          {needsLotConfirm ? (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 space-y-3">
              <p className="text-sm text-foreground">{t('caseVersion.lotAbandonWarning')}</p>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmAbandonLot}
                  onChange={(e) => setConfirmAbandonLot(e.target.checked)}
                  className="mt-0.5 rounded border-border"
                />
                <span>{t('caseVersion.lotAbandonConfirm')}</span>
              </label>
            </div>
          ) : null}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} fullWidth>
              {t('caseVersion.cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting || !canSubmit}
              fullWidth
            >
              {isSubmitting
                ? t('caseVersion.creating')
                : t('caseVersion.createButton', { version: nextVersion })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewConceptionVersionModal;
